// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewLiveFindings.test.tsx
import { Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCommentJudgmentActions } from "./AskV2ReviewCommentJudgmentActions";
import { AskV2ReviewRejectScopePrompt } from "./AskV2ReviewRejectScopePrompt";
import {
  citedRuleBrand,
  citedRuleScopeChoices,
  type RejectionScope,
} from "./rememberedSentence";

import { type StreamingVerifiedFinding } from "../../hooks/useAskPunttStream";
import {
  reviewCommentGroup,
  type ReviewCommentVisibilityFields,
} from "../../utils/visibleReviewComments";

/**
 * The saved comment a row joins to: what `liveFindingRows` needs from it, and
 * the fields `reviewCommentGroup` reads, so the row can ask the same question
 * of it the finished card asks.
 */
export type LiveFindingComment = ReviewCommentVisibilityFields & {
  _id: string;
  citations?: readonly unknown[];
};

/**
 * One live finding, joined to the comment it has become.
 *
 * `commentId` is the join and the whole point: the row is drawn from the socket
 * frame (warhol publishes the moment its evidence verifier accepts the
 * finding), and the comment is written by christo a poll later. Before that
 * write the row has nothing to open — the finding is real but there is no
 * comment to judge yet — so it is drawn as a plain row rather than a dead link.
 */
export type LiveFindingRow = {
  /** warhol's `intent_id`: what the stream entry and the saved comment share. */
  intentId: string;
  /** warhol's own severity ladder, `critical` | `major` | `minor` | `check`. */
  severity: string;
  description: string;
  /** The saved comment's `_id`, once the provisional review carries one. */
  commentId?: string;
  /** That comment's own verdict, so the heart and the delete draw its state. */
  liked?: boolean | null;
  /**
   * That comment is rejected — here, or in a past review, which leaves `liked`
   * null. The card's own `reviewCommentGroup`, so both surfaces offer Restore
   * on the same finding rather than Delete.
   */
  rejected?: boolean;
  /** Its cited rules: what a rejection's scope prompt offers to remember. */
  citations?: readonly unknown[];
};

/**
 * Urgent first, joined by intent id.
 *
 * Three ranks, not four: `critical` and `major` are one group — warhol's own
 * ladder calls both "cannot ship without this" (`isRequired` in christo's
 * `warholReview.ts`) — and the rail's live list draws them the same way, so the
 * two views of one list do not read in different orders.
 */
const RANK: Record<string, number> = {
  critical: 3,
  major: 2,
  minor: 1,
  check: 0,
};

function descriptionOf(finding: unknown): string {
  if (typeof finding !== "object" || finding === null) return "";
  const record = finding as Record<string, unknown>;
  const words =
    typeof record.observation === "string"
      ? record.observation
      : typeof record.required_action === "string"
        ? record.required_action
        : "";
  return words.replace(/\s+/g, " ").trim();
}

export function liveFindingRows(
  findings: readonly StreamingVerifiedFinding[] | undefined,
  comments: readonly LiveFindingComment[] | undefined,
): LiveFindingRow[] {
  const entries = findings ?? [];
  if (entries.length === 0) return [];

  const commentByIntent = new Map<string, LiveFindingComment>();
  for (const comment of comments ?? []) {
    if (comment?.intentId && !commentByIntent.has(comment.intentId)) {
      commentByIntent.set(comment.intentId, comment);
    }
  }

  const rows: LiveFindingRow[] = [];
  const seen = new Set<string>();
  for (const entry of entries) {
    const intentId = entry?.intentId;
    if (!intentId || seen.has(intentId)) continue;
    const description = descriptionOf(entry.finding);
    if (!description) continue;
    seen.add(intentId);
    const comment = commentByIntent.get(intentId);
    rows.push({
      intentId,
      severity: entry.severity ?? "",
      description,
      ...(comment
        ? {
            commentId: comment._id,
            liked: comment.liked,
            // why the card's own predicate and not `liked === false`: a finding
            // a past review rejected carries `liked: null`, and reading only
            // `liked` drew Delete on a row the card below drew Restore on — a
            // press then re-recorded the rejection instead of lifting it.
            rejected: reviewCommentGroup(comment) === "rejected",
            citations: comment.citations,
          }
        : {}),
    });
  }

  return rows
    .map((row, index) => ({ row, index }))
    .sort(
      (left, right) =>
        (RANK[right.row.severity] ?? -1) - (RANK[left.row.severity] ?? -1) ||
        left.index - right.index,
    )
    .map((entry) => entry.row);
}

/**
 * What the review has found so far, above the comments it will become.
 *
 * The review opens on the provisional document christo writes while the turn
 * runs, so the comments she can judge are already there — but they are only the
 * ones warhol has PUBLISHED, and the gap between "verified" and "written" is
 * one poll. This list closes it: the finding she just watched appear is on
 * screen, and the moment its comment lands the row becomes the way to it.
 *
 * The copy is the rail's (`askV2.rail.foundSoFar` and friends) on purpose. It is
 * the same list, the same words, drawn in the two places she can be looking;
 * a second set of keys would be two strings to keep in step and the first
 * place they would drift is the language nobody on the team reads.
 *
 * Each joined row also carries the judge control, because a review hands her
 * the feed thread WITHOUT its early findings (`AskV2ReviewWorkspace` mounts the
 * thread with `showEarlyFindings` off), so this list is the only live one on
 * screen — a finding she can read here and only act on a poll later is the
 * promise half kept. The control is the same `AskV2ReviewCommentJudgmentActions`
 * the finished cards draw, over the same workspace handler, so one judgment has
 * one writer and a candidate judged mid-turn writes exactly what judging it
 * afterwards would.
 */
export function AskV2ReviewLiveFindings({
  findings,
  comments,
  onOpenComment,
  onJudge,
  judgmentPending = false,
}: {
  findings: readonly StreamingVerifiedFinding[] | undefined;
  comments: readonly LiveFindingComment[] | undefined;
  onOpenComment: (commentId: string) => void;
  /**
   * The workspace's own judge handler, withheld for a read-only viewer (the
   * POST 403s for them) — the same refusal the finished cards make by not
   * receiving `onJudge`. Absent means no control is drawn at all.
   */
  onJudge?: (
    commentId: string,
    liked: boolean | null,
    scope?: RejectionScope,
  ) => void;
  judgmentPending?: boolean;
}) {
  const { t } = useTranslation("ui");
  // Which row is asking what a delete will remember. One at a time, the same
  // as the comment list's own prompt.
  const [rejectingCommentId, setRejectingCommentId] = useState<string | null>(
    null,
  );
  const rows = liveFindingRows(findings, comments);
  if (rows.length === 0) return null;

  // why: the control's own `onJudge` gate is "absent means draw nothing", and
  // the row wraps its handler -- so the gate has to be made here, where the
  // prop is still absent, or a read-only viewer is drawn a dead button.
  const judge = onJudge;

  const confidence = (severity: string) =>
    severity === "critical" || severity === "major"
      ? t("highConfidence")
      : severity === "minor"
        ? t("mediumConfidence")
        : t("lowConfidence");

  return (
    <div
      className="mx-4 mt-3"
      data-testid="ask-v2-review-found-so-far"
      role="status"
      aria-live="polite"
    >
      <Text
        as="p"
        size="1"
        weight="medium"
        color="gray"
        data-testid="ask-v2-review-found-so-far-title"
      >
        {t("askV2.rail.foundSoFar")}
      </Text>
      <ul className="mt-1.5 flex flex-col gap-1.5">
        {rows.map((row) => {
          const label = t("askV2.rail.findingAria", {
            severity: confidence(row.severity),
            description: row.description,
          });
          const body = (
            <>
              <Text as="span" size="1" weight="medium" color="gray">
                {confidence(row.severity)}
              </Text>
              <span className="min-w-0 leading-snug">{row.description}</span>
            </>
          );
          const commentId = row.commentId;
          return (
            <li
              key={row.intentId}
              className="flex flex-col gap-1 text-[12px] text-puntt-neutral-gray-12"
              data-testid={`ask-v2-review-live-finding-${row.intentId}`}
            >
              <div className="flex items-start gap-2">
                {commentId ? (
                  <button
                    type="button"
                    className="flex items-start gap-2 rounded-sm text-left outline-none hover:underline focus-visible:ring-1 focus-visible:ring-puntt-accent-8"
                    aria-label={t("askV2.rail.findingOpenAria", {
                      description: row.description,
                    })}
                    data-testid={`ask-v2-review-live-finding-link-${row.intentId}`}
                    onClick={() => onOpenComment(commentId)}
                  >
                    {body}
                  </button>
                ) : (
                  <span className="flex items-start gap-2" aria-label={label}>
                    {body}
                  </span>
                )}
                {/* why: a sibling of the open button, never inside it. The row's
                    body is a `<button>`, and a control nested in one is invalid
                    markup whose clicks the browser hands to the outer button
                    (the rule `AskV2ReviewCommentsList` carries for its cards). */}
                {commentId && judge ? (
                  <AskV2ReviewCommentJudgmentActions
                    commentId={commentId}
                    // why: the card for this same comment is drawn below this
                    // row, so the control needs its own ids or the two collide.
                    testIdPrefix="ask-v2-review-live-finding"
                    liked={row.liked}
                    // why the card's own state: this row and the card for the
                    // same comment sit in one column, so a finding rejected in
                    // a past review must offer Restore on both.
                    rejected={row.rejected === true}
                    disabled={judgmentPending}
                    // why: always drawn, never on hover -- this list is a reading
                    // surface with no per-row hover chrome, and the point of the
                    // control is acting while the rest of the review still runs.
                    pinned
                    onJudge={(liked) => {
                      setRejectingCommentId(null);
                      judge(commentId, liked);
                    }}
                    onRequestDelete={() => setRejectingCommentId(commentId)}
                  />
                ) : null}
              </div>
              {commentId && judge && rejectingCommentId === commentId ? (
                <AskV2ReviewRejectScopePrompt
                  commentId={commentId}
                  findingText={row.description}
                  brand={citedRuleBrand(row.citations)}
                  choices={citedRuleScopeChoices(row.citations)}
                  onConfirm={(scope) => {
                    setRejectingCommentId(null);
                    judge(commentId, false, scope);
                  }}
                  onCancel={() => setRejectingCommentId(null)}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
